import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { MAX_STEPS, type Step } from '@personalizehq/editor';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';

// Minimal multi-step (spec §1): a switcher over one artboard at a time. Rendering the steps SIDE BY
// SIDE as an arena is E4 — this is the strip it later becomes the tab bar of.

interface StepsStripProps {
  steps: Step[];
  focusedStepId: string;
  onFocus: (id: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onMove: (id: string, index: number) => void;
  addDisabledReason?: string | undefined;
}

export function StepsStrip({
  steps,
  focusedStepId,
  onFocus,
  onAdd,
  onRemove,
  onMove,
  addDisabledReason,
}: StepsStripProps): React.JSX.Element {
  return (
    <div className="flex min-w-0 items-center gap-1">
      <span className="mr-1 text-[10px] font-semibold uppercase text-muted-foreground">Flow</span>
      {steps.map((step, index) => {
        const active = step.id === focusedStepId;
        return (
          <div key={step.id} className="flex min-w-0 items-center gap-1">
            {index > 0 ? <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/50" /> : null}
            <div
              className={cn(
                'group flex h-8 min-w-0 items-center rounded-md border text-[12px] transition-colors',
                active
                  ? 'border-border bg-secondary text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
              )}
            >
              <button
                type="button"
                className="flex min-w-0 items-center gap-1.5 py-1 pl-2 pr-2"
                onClick={() => onFocus(step.id)}
              >
                <span
                  className={cn(
                    'grid size-4 shrink-0 place-items-center rounded-full text-[9px] font-semibold',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  )}
                >
                  {index + 1}
                </span>
                <span className="max-w-28 truncate">{step.name}</span>
              </button>

              {active ? (
                <span className="mr-1 flex items-center border-l pl-1 text-muted-foreground">
                  <button
                    type="button"
                    aria-label={`Move ${step.name} earlier`}
                    title="Move earlier"
                    disabled={index === 0}
                    className="rounded p-0.5 hover:bg-background disabled:opacity-30"
                    onClick={() => onMove(step.id, index - 1)}
                  >
                    <ChevronLeft className="size-3" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${step.name} later`}
                    title="Move later"
                    disabled={index === steps.length - 1}
                    className="rounded p-0.5 hover:bg-background disabled:opacity-30"
                    onClick={() => onMove(step.id, index + 1)}
                  >
                    <ChevronRight className="size-3" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${step.name}`}
                    title="Delete step"
                    disabled={steps.length === 1}
                    className="rounded p-0.5 hover:bg-background hover:text-destructive disabled:opacity-30"
                    onClick={() => onRemove(step.id)}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ) : null}
            </div>
          </div>
        );
      })}

      <Button
        variant="ghost"
        size="icon"
        aria-label="Add step"
        title={addDisabledReason ?? 'Add step'}
        className="ml-1 size-8 border border-dashed text-muted-foreground"
        disabled={steps.length >= MAX_STEPS || Boolean(addDisabledReason)}
        onClick={onAdd}
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
